
import { defineComponent, onBeforeMount, reactive, toRaw } from 'vue'

// components
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import Alert from '@/common/components/UI/Alert/index.vue'

// quill
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import {
    VacanciesCategoryT,
    VacanciesExperienceT,
    VacanciesRegionT,
} from '@/core/types/vacancies.types'

// realisations
import { vacancies } from '@/core/realisations/vacancies.realisation'

// validations
import { ListActionsValidation } from '@/modules/VacanciesList/validations/VacanciesListActions.validation'
import useVuelidate from '@vuelidate/core'

export default defineComponent({
    components: {
        'the-input': TheInput,
        'the-select': TheSelect,
        alert: Alert,
        'quill-editor': QuillEditor,
        'the-button': TheButton,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['submit', 'cancel'],
    setup(_, { emit }) {
        const state = reactive({
            name: null as string | null,
            salary_from: null as number | null,
            salary_to: null as number | null,
            experience_id: null as number | null,
            region_id: null as number | null,
            category_id: null as number | null,
            description: null as string | null,
        })

        const state_form$ = useVuelidate(ListActionsValidation, state)

        const data_state = reactive({
            regions: new DataState<VacanciesRegionT>({}),
            experience: new DataState<VacanciesExperienceT>({}),
            categories: new DataState<VacanciesCategoryT>({}),
        })

        const loadRegions = async (page: number) => {
            data_state.regions.pagination.current_page = page

            try {
                data_state.regions.is_loading = true
                const data = await vacancies.getRegionsList(page)

                data_state.regions.list = data.data
                data_state.regions.pagination.total_pages = data.meta.last_page
                data_state.regions.pagination.total_items = data.meta.total

                data_state.regions.is_failed = false
                data_state.regions.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.regions.failure.catchError(error)

                if (!data_state.regions.failure.error_state.is_cancel) {
                    data_state.regions.is_failed = true
                }
            } finally {
                if (!data_state.regions.failure.error_state.is_cancel) {
                    data_state.regions.is_loading = false
                }
            }
        }

        const loadMoreRegions = async (page: number) => {
            if (page > data_state.regions.pagination.total_pages) return
            data_state.regions.pagination.current_page = page

            try {
                data_state.regions.is_loading = true
                const data = await vacancies.getRegionsList(page)

                data_state.regions.list = data_state.regions.list.concat(data.data)
                data_state.regions.pagination.total_pages = data.meta.last_page
                data_state.regions.pagination.total_items = data.meta.total

                data_state.regions.is_failed = false
                data_state.regions.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.regions.failure.catchError(error)

                if (!data_state.regions.failure.error_state.is_cancel) {
                    data_state.regions.is_failed = true
                }
            } finally {
                if (!data_state.regions.failure.error_state.is_cancel) {
                    data_state.regions.is_loading = false
                }
            }
        }

        const loadExperience = async (page: number) => {
            data_state.experience.pagination.current_page = page

            try {
                data_state.experience.is_loading = true
                const data = await vacancies.getExperienceList(page)

                data_state.experience.list = data.data
                data_state.experience.pagination.total_pages = data.meta.last_page
                data_state.experience.pagination.total_items = data.meta.total

                data_state.experience.is_failed = false
                data_state.experience.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.experience.failure.catchError(error)

                if (!data_state.experience.failure.error_state.is_cancel) {
                    data_state.experience.is_failed = true
                }
            } finally {
                if (!data_state.experience.failure.error_state.is_cancel) {
                    data_state.experience.is_loading = false
                }
            }
        }

        const loadMoreExperience = async (page: number) => {
            if (page > data_state.experience.pagination.total_pages) return
            data_state.experience.pagination.current_page = page

            try {
                data_state.experience.is_loading = true
                const data = await vacancies.getExperienceList(page)

                data_state.experience.list = data_state.experience.list.concat(data.data)
                data_state.experience.pagination.total_pages = data.meta.last_page
                data_state.experience.pagination.total_items = data.meta.total

                data_state.experience.is_failed = false
                data_state.experience.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.experience.failure.catchError(error)

                if (!data_state.experience.failure.error_state.is_cancel) {
                    data_state.experience.is_failed = true
                }
            } finally {
                if (!data_state.experience.failure.error_state.is_cancel) {
                    data_state.experience.is_loading = false
                }
            }
        }

        const loadCategories = async (page: number) => {
            data_state.categories.pagination.current_page = page

            try {
                data_state.categories.is_loading = true
                const data = await vacancies.getCategoriesList(page)

                data_state.categories.list = data.data
                data_state.categories.pagination.total_pages = data.meta.last_page
                data_state.categories.pagination.total_items = data.meta.total

                data_state.categories.is_failed = false
                data_state.categories.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.categories.failure.catchError(error)

                if (!data_state.categories.failure.error_state.is_cancel) {
                    data_state.categories.is_failed = true
                }
            } finally {
                if (!data_state.categories.failure.error_state.is_cancel) {
                    data_state.categories.is_loading = false
                }
            }
        }

        const loadMoreCategories = async (page: number) => {
            if (page > data_state.experience.pagination.total_pages) return
            data_state.categories.pagination.current_page = page

            try {
                data_state.categories.is_loading = true
                const data = await vacancies.getCategoriesList(page)

                data_state.categories.list = data_state.categories.list.concat(data.data)
                data_state.categories.pagination.total_pages = data.meta.last_page
                data_state.categories.pagination.total_items = data.meta.total

                data_state.categories.is_failed = false
                data_state.categories.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.categories.failure.catchError(error)

                if (!data_state.categories.failure.error_state.is_cancel) {
                    data_state.categories.is_failed = true
                }
            } finally {
                if (!data_state.categories.failure.error_state.is_cancel) {
                    data_state.categories.is_loading = false
                }
            }
        }

        const emitSubmit = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            const payload = {
                ...state,
                salary_from: Number(state.salary_from),
                salary_to: Number(state.salary_to),
            }

            emit('submit', toRaw(payload))
        }

        const emitCancel = () => {
            emit('cancel')
        }

        onBeforeMount(() => {
            loadRegions(1)
            loadExperience(1)
            loadCategories(1)
        })

        return {
            state,
            state_form$,
            data_state,
            loadRegions,
            loadMoreRegions,
            loadExperience,
            loadMoreExperience,
            loadCategories,
            loadMoreCategories,
            emitSubmit,
            emitCancel,
        }
    },
})
